import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import {
  getAdminSaleEvent,
  createSaleEvent,
  updateSaleEvent,
  type AdminSaleEvent,
} from '../lib/api'
import AssetImageField from '../components/AssetImageField'

interface FormState {
  title: string
  description: string
  date: string
  time: string
  location: string
  banner_image_url: string
  is_published: boolean
  terms: string
}

const blank: FormState = {
  title: '',
  description: '',
  date: '',
  time: '',
  location: '',
  banner_image_url: '',
  is_published: false,
  terms: '',
}

function toFormState(e: AdminSaleEvent): FormState {
  return {
    title: e.title,
    description: e.description,
    date: e.date,
    time: e.time ?? '',
    location: e.location,
    banner_image_url: e.banner_image_url ?? '',
    is_published: e.is_published,
    terms: e.terms ?? '',
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

export default function SaleEventFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const { token } = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(blank)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEdit)

  useEffect(() => {
    if (isEdit && id && token) {
      getAdminSaleEvent(token, id)
        .then((e) => setForm(toFormState(e)))
        .catch(console.error)
        .finally(() => setLoading(false))
    }
  }, [isEdit, id, token])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      const payload = {
        title: form.title,
        description: form.description,
        date: form.date,
        time: form.time || null,
        location: form.location,
        banner_image_url: form.banner_image_url || null,
        is_published: form.is_published,
        terms: form.terms || null,
      }

      if (isEdit && id) {
        await updateSaleEvent(token, id, payload)
        navigate(`/sales/sale-events/${id}/overview`)
      } else {
        const created = await createSaleEvent(token, payload)
        navigate(`/sales/sale-events/${created.id}/overview`)
      }
    } catch (err) {
      alert('Gagal menyimpan event: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setSaving(false)
    }
  }

  const label = 'block text-xs text-zinc-400 mb-1.5'
  const input = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
            >
              <ChevronLeft size={14} />
              Kembali
            </button>
            <h1 className="text-xl font-bold text-white">
              {isEdit ? 'Edit Sale Event' : 'New Sale Event'}
            </h1>
            {isEdit && <p className="text-zinc-500 text-sm mt-1">{form.title}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat Event'}
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">

          {/* ── Informasi Dasar ─────────────────────────────── */}
          <Section title="Informasi Dasar">
            <div>
              <label className={label}>Judul <span className="text-red-400">*</span></label>
              <input
                type="text"
                className={input}
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Nama sale event"
                required
              />
            </div>

            <div>
              <label className={label}>Deskripsi <span className="text-red-400">*</span></label>
              <textarea
                className={input + ' resize-y'}
                rows={4}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Deskripsi lengkap tentang event ini"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Tanggal <span className="text-red-400">*</span></label>
                <input
                  className={input + ' [color-scheme:dark]'}
                  type="date"
                  value={form.date}
                  onChange={(e) => set('date', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className={label}>
                  Waktu{' '}
                  <span className="text-zinc-600 font-normal normal-case tracking-normal">— opsional</span>
                </label>
                <input
                  className={input + ' [color-scheme:dark]'}
                  type="time"
                  value={form.time}
                  onChange={(e) => set('time', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className={label}>Lokasi <span className="text-red-400">*</span></label>
              <input
                type="text"
                className={input}
                value={form.location}
                onChange={(e) => set('location', e.target.value)}
                placeholder="Nama venue atau kota"
                required
              />
            </div>
          </Section>

          {/* ── Syarat dan Ketentuan ────────────────────────── */}
          <Section title="Syarat dan Ketentuan">
            <div>
              <label className={label}>
                Syarat dan Ketentuan{' '}
                <span className="text-zinc-600 font-normal normal-case tracking-normal">— opsional</span>
              </label>
              <textarea
                className={input + ' resize-y'}
                rows={6}
                value={form.terms}
                onChange={(e) => set('terms', e.target.value)}
                placeholder="Tuliskan syarat dan ketentuan pembelian tiket event ini"
              />
            </div>
          </Section>

          {/* ── Visual ──────────────────────────────────────── */}
          <Section title="Visual">
            <div>
              <label className={label}>
                Banner Event{' '}
                <span className="text-zinc-600 font-normal normal-case tracking-normal">— opsional</span>
              </label>
              <AssetImageField
                value={form.banner_image_url}
                onChange={(url) => set('banner_image_url', url)}
                label="Banner Event"
                feature="sale-events"
              />
            </div>
          </Section>

          {/* ── Pengaturan ──────────────────────────────────── */}
          <Section title="Pengaturan">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white font-medium">Status Event</p>
                <p className="text-xs text-zinc-500 mt-0.5">Event yang dipublikasikan akan tampil di halaman publik</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => set('is_published', !form.is_published)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    form.is_published ? 'bg-green-500' : 'bg-zinc-600'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                      form.is_published ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`text-xs font-medium w-14 ${form.is_published ? 'text-green-400' : 'text-zinc-500'}`}>
                  {form.is_published ? 'Public' : 'Draft'}
                </span>
              </div>
            </div>
          </Section>

        </div>
      </form>
    </div>
  )
}
